import * as React from "react"
import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"

type PageProps = {
  manufacturer: {
    name: string
    country: string | null
    notes: string | null
    is_own: boolean
  }
}

const DESCRIPTION =
  "Tambah manufaktur baru ke registry — perusahaan sendiri atau kompetitor. Nama kanonik dihitung otomatis dari nama."

export default function New({ manufacturer }: PageProps) {
  const { data, setData, post, processing, errors } = useForm({
    name: manufacturer.name ?? "",
    country: manufacturer.country ?? "",
    notes: manufacturer.notes ?? "",
    is_own: manufacturer.is_own ?? false,
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/admin/manufacturers")
  }

  return (
    <>
      <Head title="Manufaktur baru">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Manufaktur baru" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Intelligence</span>
          <span>/</span>
          <Link href="/admin/manufacturers" className="hover:text-ink-body">
            Manufaktur
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">Baru</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <h1>Manufaktur baru</h1>
          <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
        </div>

        <form onSubmit={onSubmit} className="mt-6 max-w-2xl">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="name">Nama manufaktur</label>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                placeholder="cth. Unilever"
                className="mt-1"
              />
              {errors.name && <p className="mt-1 text-sm text-danger">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="country">Negara (opsional)</label>
              <Input
                id="country"
                value={data.country}
                onChange={(e) => setData("country", e.target.value)}
                placeholder="cth. Indonesia"
                className="mt-1"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="notes">Catatan (opsional)</label>
              <textarea
                id="notes"
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                rows={3}
                className="form-control mt-1"
                placeholder="Konteks internal tentang manufaktur ini."
              />
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 font-normal text-ink-body">
                <Checkbox
                  checked={data.is_own}
                  onChange={(e) => setData("is_own", e.target.checked)}
                />
                Perusahaan sendiri
              </label>
              <p className="mt-1 text-xs text-ink-muted">
                Tandai jika ini perusahaan kita (bukan kompetitor).
              </p>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-3 border-t border-hairline pt-6">
            <Button type="submit" disabled={processing}>
              Simpan manufaktur
            </Button>
            <Button asChild variant="ghost">
              <Link href="/admin/manufacturers">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Batal
              </Link>
            </Button>
          </div>
        </form>
      </AppShell>
    </>
  )
}
